import React from "react";
import { ArrowRight, Cpu, FileCheck2, FileDown, Headphones } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
const supportCopy = {
  eyebrow: {
    en: "Engineering & Download Support",
    zh: "工程与下载支持",
    es: "Soporte de ingeniería y descargas",
    de: "Ingenieur- & Download-Support",
    fr: "Support d'ingénierie et téléchargements",
    pt: "Suporte de engenharia e downloads"
  },
  h2: {
    en: "Need a custom specification or technical data pack?",
    zh: "需要定制规格或完整技术数据包？",
    es: "¿Necesita una especificación personalizada o un paquete de datos técnicos?",
    de: "Benötigen Sie eine kundenspezifische Spezifikation oder ein technisches Datenpaket?",
    fr: "Besoin d'une spécification sur mesure ou d'un dossier technique complet ?",
    pt: "Precisa de uma especificação personalizada ou pacote de dados técnicos?"
  },
  lead: {
    en: "Our engineering team prepares application-specific micron ratings, pressure drop calculations, and verified alloy datasheets tailored to your filtration requirements.",
    zh: "我们的工程团队可根据您的过滤要求定制专用微米精度评级、压降计算及经认证的合金技术数据表。",
    es: "Nuestro equipo de ingeniería prepara clasificaciones en micras para aplicaciones específicas, cálculos de caída de presión y fichas de aleaciones verificadas según sus requisitos de filtración.",
    de: "Unser Ingenieurteam erstellt anwendungsspezifische Mikron-Abscheidegrade, Druckverlustberechnungen und geprüfte Legierungsdatenblätter, die auf Ihre Filtrationsanforderungen zugeschnitten sind.",
    fr: "Notre équipe d'ingénierie élabore des seuils de coupure en microns, des calculs de perte de charge et des fiches d'alliages certifiés adaptés à vos exigences de filtration.",
    pt: "Nossa equipe de engenharia elabora classificações em mícrons para aplicações específicas, cálculos de queda de pressão e fichas técnicas de ligas verificadas para seus requisitos de filtração."
  },
  consultFormBtn: {
    en: "Go to consultation form",
    zh: "填写工程技术咨询表",
    es: "Ir al formulario de consulta",
    de: "Zum Beratungsformular",
    fr: "Aller au formulaire de consultation",
    pt: "Ir para o formulário de consultoria"
  },
  ctaSecondary: {
    en: "Browse technical library",
    zh: "浏览技术资源库",
    es: "Explorar biblioteca técnica",
    de: "Technische Bibliothek durchsuchen",
    fr: "Parcourir la bibliothèque technique",
    pt: "Navegar pela biblioteca técnica"
  }
};
const supportHighlights = [{
  id: 1,
  icon: FileCheck2,
  title: {
    en: "Material test reports",
    zh: "材质报告与合规单",
    es: "Informes de ensayo de materiales",
    de: "Materialprüfberichte",
    fr: "Rapports d'essais matériaux",
    pt: "Relatórios de teste de materiais"
  },
  desc: {
    en: "Material test reports and alloy verification documentation available when specified in the order.",
    zh: "出厂材质检测报告及合金成分验证报告可按订单合同要求提供。",
    es: "Informes de ensayo de materiales y documentación de verificación de aleaciones disponibles cuando se especifiquen en el pedido.",
    de: "Materialprüfberichte und Legierungsverifizierungsunterlagen auf Bestellanfrage lieferbar.",
    fr: "Rapports d'essais matériaux et documents de contrôle des alliages disponibles sur demande à la commande.",
    pt: "Relatórios de teste de materiais e documentação de verificação de ligas disponíveis quando especificados no pedido."
  }
}, {
  id: 2,
  icon: Cpu,
  title: {
    en: "Custom CAD & dimensional drawings",
    zh: "定制 CAD 与尺寸图纸",
    es: "CAD y planos dimensionales personalizados",
    de: "Individuelle CAD- & Maßzeichnungen",
    fr: "CAD et plans dimensionnels sur mesure",
    pt: "CAD e desenhos dimensionais sob medida"
  },
  desc: {
    en: "Detailed aperture geometry, wire tolerances, and fabrication schematics.",
    zh: "详细的网孔几何结构、丝径公差与加工制造装配示意图。",
    es: "Geometría detallada de aperturas, tolerancias de alambre y esquemas de fabricación.",
    de: "Detaillierte Maschengeometrie, Drahttoleranzen und Fertigungsschemata.",
    fr: "Géométrie détaillée des mailles, tolérances de fil et schémas de fabrication.",
    pt: "Geometria detalhada de aberturas, tolerâncias de arame e esquemas de fabricação."
  }
}, {
  id: 3,
  icon: Headphones,
  title: {
    en: "Application engineering support",
    zh: "应用工程技术支持",
    es: "Soporte de ingeniería de aplicaciones",
    de: "Support für Anwendungstechnik",
    fr: "Support en ingénierie d'application",
    pt: "Suporte de engenharia de aplicação"
  },
  desc: {
    en: "Technical guidance on flow velocity, differential pressure, and media selection.",
    zh: "针对工作流速、差压损失及过滤网介质选型的直接工程指导。",
    es: "Orientación técnica sobre velocidad de flujo, presión diferencial y selección de medios.",
    de: "Technische Beratung zu Strömungsgeschwindigkeit, Differenzdruck und Medienauswahl.",
    fr: "Conseils techniques sur la vitesse d'écoulement, la perte de charge et le choix du média.",
    pt: "Orientação técnica sobre velocidade de fluxo, pressão diferencial e seleção de meios."
  }
}];
export default function DownloadSupport() {
  const {
    lang
  } = useLanguage();
  return <section id="downloadsupportcta" data-nav="light" className="relative bg-background mf-section border-t border-border overflow-hidden">
      <div className="mf-container">
        <Reveal>
          <div className="relative rounded-lg bg-surface-dark text-surface-dark-foreground overflow-hidden p-8 sm:p-12 lg:p-16 border border-surface-dark-border">
            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6">
              <div className="flex justify-center">
                <Badge variant="outline" className="border-surface-dark-border text-surface-dark-foreground bg-surface-dark-elevated px-3.5 py-1 mf-caption uppercase tracking-wider font-mono">
                  {pick(lang, supportCopy.eyebrow)}
                </Badge>
              </div>

              <h2 className="mf-h2 tracking-tight text-surface-dark-foreground font-default">
                {pick(lang, supportCopy.h2)}
              </h2>

              <p className="mf-lead text-surface-dark-muted max-w-2xl mx-auto leading-relaxed">
                {pick(lang, supportCopy.lead)}
              </p>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 pb-2 text-left">
                {supportHighlights.map((item, index) => {
                const IconComponent = item.icon;
                return <div key={item.id} data-index={index} className="p-4 rounded-lg bg-surface-dark-elevated border border-surface-dark-border space-y-2 transition-[border-color,background-color] hover:bg-surface-dark-elevated/90">
                      <div className="flex items-center gap-2.5">
                        <div className="inline-flex size-10 items-center justify-center rounded-md border border-surface-dark-border bg-surface-dark text-accent">
                          <IconComponent className="size-5" aria-hidden="true" />
                        </div>
                        <h3 className="text-sm font-semibold text-surface-dark-foreground">
                          {pick(lang, item.title)}
                        </h3>
                      </div>
                      <p className="mf-caption text-surface-dark-muted leading-relaxed">
                        {pick(lang, item.desc)}
                      </p>
                    </div>;
              })}
              </div>

              {/* Actions */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 transition-colors">
                  <Link to="#technicalconsultation" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, supportCopy.consultFormBtn)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-surface-dark-border text-surface-dark-foreground hover:bg-surface-dark-elevated hover:text-surface-dark-foreground font-medium px-7 transition-colors">
                  <Link to="/TechnicalResourcesPage" className="inline-flex items-center justify-center gap-2">
                    <FileDown className="size-4" aria-hidden="true" />
                    <span>{pick(lang, supportCopy.ctaSecondary)}</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}